/**
 * Das schmale Protokoll zwischen Portal-Shell und eingebetteter Fremdoberfläche.
 *
 * ADR-040 verlangt Tiefverweise **in beide Richtungen**: aus dem Rahmen in die
 * Auftragsakte und aus dem Portal in eine bestimmte Nachricht. Dafür muss der
 * eingebettete Dienst seinen Ort nach oben melden können, und die Shell muss
 * ihn in ihre eigene Adresse übernehmen — sonst landen Neuladen und Lesezeichen
 * an einer anderen Stelle als der, die der Benutzer gerade ansieht.
 *
 * Diese Datei enthält **nur die Prüfung**, kein React und kein DOM. Der Grund
 * ist nicht Ordnungssinn: Der gefährliche Teil an `postMessage` ist die
 * Herkunftsprüfung, und eine Prüfung, die nur im Zusammenspiel mit einem
 * gerenderten Rahmen läuft, wird nicht mit Grenzfällen getestet.
 *
 * **Jede Seite im Rahmen kann Nachrichten schicken — auch eine, die dort nicht
 * hingehört.** Ein eingebetteter Dienst, der eine fremde Seite nachlädt, ein
 * Werbebanner, eine untergeschobene Weiterleitung: Sie alle erreichen
 * `window.message`. Wer die Herkunft nicht prüft, lässt eine beliebige Seite
 * die Adresse des Portals umschreiben, und das ist der erste Schritt zu einem
 * Verweis, der woandershin führt, als er vorgibt.
 */

/** Was ein eingebetteter Dienst schicken darf. Mehr gibt es nicht. */
export type Rahmennachricht =
  | { readonly typ: "bereit" }
  | { readonly typ: "ort"; readonly wert: string };

export interface Pruefergebnis {
  readonly nachricht: Rahmennachricht | null;
  /** Warum verworfen wurde. Leer, wenn angenommen. */
  readonly verworfen: string;
}

/**
 * Ein Ort ist ein Bezeichner, kein Freitext und keine Adresse.
 *
 * Absichtlich eng: Er landet in der Adresszeile der Shell. Ein Ort, der
 * Schrägstriche oder einen Doppelpunkt enthalten dürfte, wäre der Weg, über
 * den eine eingebettete Seite die Route der Shell umschreibt.
 */
const ORT_MUSTER = /^[A-Za-z0-9_.-]{1,128}$/;

/**
 * Prüft eine eingegangene Nachricht gegen die erwartete Herkunft.
 *
 * `herkunft` ist der volle Ursprung des eingebetteten Dienstes — Schema, Host
 * und Port, so wie ihn `new URL(dienstAdresse).origin` liefert. Nicht der
 * Hostname allein: `https://mail.kunde.de` und `http://mail.kunde.de:8080`
 * unterscheiden sich genau dort, wo es darauf ankommt.
 */
export function nachrichtPruefen(
  daten: unknown,
  herkunftGemeldet: string,
  herkunftErwartet: string,
): Pruefergebnis {
  if (herkunftErwartet === "" || herkunftErwartet === "*") {
    // Keine Bequemlichkeit erlauben: "*" ist die Einstellung, mit der die
    // Prüfung aufhört, eine zu sein.
    return { nachricht: null, verworfen: "keine erwartete Herkunft gesetzt" };
  }
  if (herkunftGemeldet !== herkunftErwartet) {
    return {
      nachricht: null,
      verworfen: `fremde Herkunft ${herkunftGemeldet || "(leer)"}`,
    };
  }
  if (typeof daten !== "object" || daten === null) {
    return { nachricht: null, verworfen: "kein Objekt" };
  }
  const typ = (daten as { typ?: unknown }).typ;
  if (typ === "bereit") {
    return { nachricht: { typ: "bereit" }, verworfen: "" };
  }
  if (typ === "ort") {
    const wert = (daten as { wert?: unknown }).wert;
    if (typeof wert !== "string" || !ORT_MUSTER.test(wert)) {
      return { nachricht: null, verworfen: "unzulässiger Ort" };
    }
    return { nachricht: { typ: "ort", wert }, verworfen: "" };
  }
  return { nachricht: null, verworfen: "unbekannter Typ" };
}

/**
 * Baut die Adresse des eingebetteten Dienstes für einen Ort.
 *
 * Die Gegenrichtung zu `nachrichtPruefen`: Die Shell trägt den Ort in ihrer
 * eigenen Adresse und gibt ihn dem Dienst als Suchabfrage mit, damit ein
 * Lesezeichen im Rahmen an derselben Stelle landet.
 */
export function dienstAdresse(basis: string, ort: string | null): string {
  const url = new URL(basis);
  if (ort && ORT_MUSTER.test(ort)) {
    url.searchParams.set("ort", ort);
  }
  return url.toString();
}

/** Der Ursprung einer Dienstadresse — für die Herkunftsprüfung. */
export function herkunftVon(adresse: string): string {
  return new URL(adresse).origin;
}
